const defaultBase =
  (process.env.REACT_APP_API_BASE || "https://api.techwellpro.com/trainee").replace(/\/+$/, "");

const withBase = (path) => `${defaultBase}/${path}`;

const getSessionId = () => {
  try {
    const raw = localStorage.getItem("tms-session");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.id || null;
  } catch {
    return null;
  }
};

async function request(path, options = {}) {
  const headers = { "Content-Type": "application/json", ...(options.headers || {}) };
  let bodyObj = null;

  if (options.body && typeof options.body === "string") {
    try {
      bodyObj = JSON.parse(options.body);
    } catch {
      bodyObj = null;
    }
  } else if (options.body && typeof options.body === "object") {
    bodyObj = options.body;
  }

  const sessionId = getSessionId();
  if (sessionId && bodyObj && bodyObj.user_id === undefined) {
    bodyObj.user_id = sessionId;
  }

  const finalBody = bodyObj ? JSON.stringify(bodyObj) : options.body;

  const resp = await fetch(withBase(path), {
    credentials: "include",
    headers,
    ...options,
    body: finalBody,
  });
  let data;
  try {
    data = await resp.json();
  } catch (err) {
    // Attempt to salvage responses with stray text before JSON
    try {
      const text = await resp.text();
      const start = text.indexOf("{");
      if (start !== -1) {
        data = JSON.parse(text.slice(start));
      } else {
        throw err;
      }
    } catch {
      throw new Error("Invalid server response");
    }
  }
  if (!resp.ok || data.status === false) {
    throw new Error(data?.message || "Request failed");
  }
  return data.data || data;
}

export const api = {
  login: (email, password) =>
    request("auth/login.php", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request("auth/logout.php", { method: "POST" }),
  register: (payload) =>
    request("auth/register.php", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  uploadProfilePhoto: (file, forUserId) => {
    const fd = new FormData();
    fd.append("photo", file);
    if (forUserId) fd.append("for_user_id", forUserId);
    const sid = getSessionId();
    if (sid) fd.append("user_id", sid);
    return fetch(withBase("common/upload_profile_photo.php"), {
      method: "POST",
      credentials: "include",
      body: fd,
    }).then(async (resp) => {
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data.status === false) {
        throw new Error(data.message || "Upload failed");
      }
      return data.data || data;
    });
  },
  profile: () => request("common/user_profile.php", { method: "POST", body: {} }),
  markAttendance: () => request("common/mark_attendance.php", { method: "POST", body: {} }),
  placements: () => request("trainee/placements.php", { method: "POST", body: {} }),
  notifications: () => request("trainee/notifications.php", { method: "POST", body: {} }),
  myTasks: (status) =>
    request("trainee/my_tasks.php", {
      method: "POST",
      body: status ? { status } : {},
    }),
  myApplications: () =>
    request("trainee/my_applications.php", {
      method: "POST",
      body: {},
    }),
  applyPlacement: (placement_id) =>
    request("common/apply_placement.php", {
      method: "POST",
      body: { placement_id },
    }),
  updateTaskStatus: (task_id, status) =>
    request("trainee/update_task_status.php", {
      method: "POST",
      body: { task_id, status },
    }),
  assignedTrainees: () =>
    request("trainer/assigned_trainees.php", {
      method: "POST",
      body: { user_id: getSessionId() },
    }),
  createTask: (payload) =>
    request("trainer/create_task.php", {
      method: "POST",
      body: payload,
    }),
  traineeTasks: (trainee_id) =>
    request("trainer/trainee_tasks.php", {
      method: "POST",
      body: trainee_id ? { trainee_id } : {},
    }),
  trainerAttendance: (trainee_id) =>
    request("trainer/trainee_attendance.php", {
      method: "POST",
      body: trainee_id ? { trainee_id } : {},
    }),
  adminViewUsers: () => request("admin/view_users.php", { method: "POST", body: {} }),
  adminViewAttendance: (filters) =>
    request("admin/view_attendance.php", {
      method: "POST",
      body: filters || {},
    }),
  adminCreateTrainer: (payload) =>
    request("admin/create_trainer.php", { method: "POST", body: payload }),
  adminCreateTrainee: (payload) =>
    request("admin/create_trainee.php", { method: "POST", body: payload }),
  adminAssignTrainee: (payload) =>
    request("admin/assign_trainee.php", { method: "POST", body: payload }),
  adminCreatePlacement: (payload) =>
    request("admin/create_placement.php", { method: "POST", body: payload }),
  adminUpdatePlacement: (payload) =>
    request("admin/update_placement.php", { method: "POST", body: payload }),
  adminDeletePlacement: (placement_id) =>
    request("admin/delete_placement.php", { method: "POST", body: { placement_id } }),
  adminUpdateUser: (payload) =>
    request("admin/update_user.php", { method: "POST", body: payload }),
  adminDeleteUser: (user_id) =>
    request("admin/delete_user.php", { method: "POST", body: { user_id } }),
  adminDashboard: () => request("admin/dashboard_summary.php", { method: "POST", body: {} }),
  adminPlacementApplicants: () =>
    request("admin/placement_applicants.php", { method: "POST", body: {} }),
};

export default api;
